import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, Shield, Film, Handshake, Landmark, PlayCircle,
  Search, Send, MessageCircle, CheckCircle2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import ProjectCard from "@/components/ProjectCard";
import { api } from "@/lib/api";

const featureFadeIn = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.1 + i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Landing() {
  const [featured, setFeatured] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/projects/featured?limit=6");
        setFeatured(data);
      } catch (e) {}
    })();
  }, []);

  return (
    <div className="marketing-shell">
      <MarketingNav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/8089650/pexels-photo-8089650.jpeg"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 hero-mask" />
        </div>

        <div className="relative mx-auto max-w-7xl px-6 pt-24 pb-32 lg:pt-32 lg:pb-40 grain-overlay">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="eyebrow text-gold"
          >
            {t("landing.eyebrow")}
          </motion.p>
          <motion.h1
            initial="hidden"
            animate="visible"
            variants={featureFadeIn}
            custom={1}
            className="font-display mt-6 text-white text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-4xl"
          >
            {t("landing.hero_h1_a")} <span className="italic text-gold">{t("landing.hero_h1_b")}</span>
            <br />
            {t("landing.hero_h1_c")} <span className="italic text-gold">{t("landing.hero_h1_d")}</span>
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="visible"
            variants={featureFadeIn}
            custom={2}
            className="mt-8 max-w-2xl text-white/75 text-lg leading-relaxed"
          >
            {t("landing.hero_subtitle")}
          </motion.p>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={featureFadeIn}
            custom={3}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/registro?role=creator"
              data-testid="button-hero-publish"
              className="gold-cta inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-base"
            >
              {t("landing.cta_i_am_creator")} <ArrowRight size={18} />
            </Link>
            <Link
              to="/explorar"
              data-testid="button-hero-explore"
              className="ghost-cta inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-base"
            >
              {t("landing.cta_i_am_sponsor")}
            </Link>
            <Link
              to="/como-funciona"
              className="inline-flex items-center gap-2 text-white/70 hover:text-gold self-center pl-2"
              data-testid="link-hero-how"
            >
              <PlayCircle size={16} /> {t("landing.cta_watch_how")}
            </Link>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={featureFadeIn}
            custom={4}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl"
          >
            {[
              { k: "3", l: t("landing.stat_modalities") },
              { k: "6+", l: t("landing.stat_countries") },
              { k: "20-20-15", l: t("landing.stat_matching") },
              { k: "PDF", l: t("landing.stat_scripts") },
            ].map((s) => (
              <div key={s.k} className="glass-card rounded-2xl px-5 py-4">
                <div className="font-display text-3xl text-gold">{s.k}</div>
                <div className="text-xs mt-1 text-white/60 uppercase tracking-widest">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* VALUE PROP */}
      <section className="relative border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="grid lg:grid-cols-[1fr_1fr] gap-16 items-start">
            <div>
              <p className="eyebrow text-gold">{t("landing.value_eyebrow")}</p>
              <h2 className="font-display text-4xl md:text-5xl mt-4 tracking-tight text-white">
                {t("landing.value_h2")}
              </h2>
              <p className="text-white/70 mt-6 max-w-xl">
                {t("landing.value_body")}
              </p>
              <div className="mt-8 flex items-center gap-3 text-sm text-white/60">
                <Sparkles size={16} className="text-gold" /> {t("landing.value_meta")}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div className="glass-card rounded-3xl p-8 hover:border-gold/40 transition-colors">
                <p className="eyebrow text-gold mb-3">{t("landing.role_creator_eyebrow")}</p>
                <h3 className="font-display text-3xl text-white">{t("landing.role_creator_h3")}</h3>
                <p className="text-white/70 mt-3">
                  {t("landing.role_creator_p")}
                </p>
                <Link
                  to="/registro?role=creator"
                  data-testid="button-value-creator"
                  className="mt-6 inline-flex items-center gap-2 gold-cta rounded-full px-5 py-2.5 text-sm font-semibold"
                >
                  {t("landing.role_creator_cta")} <ArrowRight size={14} />
                </Link>
              </div>
              <div className="glass-card rounded-3xl p-8 hover:border-gold/40 transition-colors">
                <p className="eyebrow text-gold mb-3">{t("landing.role_sponsor_eyebrow")}</p>
                <h3 className="font-display text-3xl text-white">{t("landing.role_sponsor_h3")}</h3>
                <p className="text-white/70 mt-3">
                  {t("landing.role_sponsor_p")}
                </p>
                <Link
                  to="/registro?role=sponsor"
                  data-testid="button-value-sponsor"
                  className="mt-6 inline-flex items-center gap-2 ghost-cta rounded-full px-5 py-2.5 text-sm font-semibold"
                >
                  {t("landing.role_sponsor_cta")} <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="como-funciona" className="relative border-t border-white/5 bg-[#080F1B]">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="max-w-2xl">
            <p className="eyebrow text-gold">{t("landing.flow_eyebrow")}</p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 tracking-tight text-white">
              {t("landing.flow_h2")}
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-display text-2xl text-gold mb-6">{t("landing.flow_creators_title")}</h3>
              <ol className="space-y-4">
                {[
                  { icon: Film, key: "flow_creator_step_1" },
                  { icon: Search, key: "flow_creator_step_2" },
                  { icon: Sparkles, key: "flow_creator_step_3" },
                  { icon: Handshake, key: "flow_creator_step_4" },
                  { icon: Send, key: "flow_creator_step_5" },
                ].map(({ key }, i) => (
                  <li key={key} className="flex items-start gap-4">
                    <span className="inline-flex h-9 w-9 rounded-full bg-gold text-petroleum items-center justify-center font-semibold text-sm mt-0.5">
                      {i + 1}
                    </span>
                    <div>
                      <p className="text-white text-base">{t(`landing.${key}`)}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <div>
              <h3 className="font-display text-2xl text-gold mb-6">{t("landing.flow_sponsors_title")}</h3>
              <ol className="space-y-4">
                {[
                  "flow_sponsor_step_1",
                  "flow_sponsor_step_2",
                  "flow_sponsor_step_3",
                  "flow_sponsor_step_4",
                  "flow_sponsor_step_5",
                ].map((key, i) => (
                  <li key={key} className="flex items-start gap-4">
                    <span className="inline-flex h-9 w-9 rounded-full border border-gold/60 text-gold items-center justify-center font-semibold text-sm mt-0.5">
                      {i + 1}
                    </span>
                    <div>
                      <p className="text-white text-base">{t(`landing.${key}`)}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* THREE PATHS */}
      <section id="modalidades" className="relative border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="eyebrow text-gold">{t("landing.paths_eyebrow")}</p>
              <h2 className="font-display text-4xl md:text-5xl mt-4 tracking-tight text-white max-w-3xl">
                {t("landing.paths_h2")}
              </h2>
            </div>
            <Link
              to="/como-funciona"
              className="text-gold inline-flex items-center gap-2 hover:underline"
              data-testid="link-conoce-modalidades"
            >
              {t("landing.paths_link")} <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Film, titleKey: "path_placement_title", textKey: "path_placement_text" },
              { icon: Handshake, titleKey: "path_sponsorship_title", textKey: "path_sponsorship_text" },
              { icon: Landmark, titleKey: "path_funds_title", textKey: "path_funds_text" },
            ].map(({ icon: Icon, titleKey, textKey }) => (
              <div key={titleKey} className="glass-card rounded-3xl p-8 hover:border-gold/50 transition-colors">
                <span className="inline-flex h-12 w-12 rounded-full bg-gold/15 text-gold items-center justify-center">
                  <Icon size={22} />
                </span>
                <h3 className="mt-6 font-display text-2xl text-white">{t(`landing.${titleKey}`)}</h3>
                <p className="text-white/70 mt-3 leading-relaxed">{t(`landing.${textKey}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="relative border-t border-white/5 bg-[#080F1B]">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <p className="eyebrow text-gold">{t("landing.featured_eyebrow")}</p>
              <h2 className="font-display text-4xl md:text-5xl mt-4 tracking-tight text-white">
                {t("landing.featured_h2")}
              </h2>
            </div>
            <Link to="/explorar" className="text-gold hover:underline inline-flex items-center gap-2" data-testid="link-explorar-todos">
              {t("landing.featured_link")} <ArrowRight size={16} />
            </Link>
          </div>

          {featured.length === 0 ? (
            <div className="text-white/50 text-sm">{t("landing.featured_loading")}</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.map((p) => (
                <ProjectCard key={p.id} project={p} theme="dark" />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* IP TRUST */}
      <section className="relative border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 py-24 grid lg:grid-cols-[1fr_1fr] gap-16 items-center">
          <div>
            <p className="eyebrow text-gold">{t("landing.ip_eyebrow")}</p>
            <h2 className="font-display text-4xl md:text-5xl mt-4 tracking-tight text-white">
              {t("landing.ip_h2_a")} <span className="italic text-gold">{t("landing.ip_h2_b")}</span>
            </h2>
            <p className="text-white/70 mt-6 leading-relaxed">
              {t("landing.ip_body")}
            </p>
            <ul className="mt-8 space-y-3">
              {[1, 2, 3, 4, 5].map((n) => (
                <li key={n} className="flex items-start gap-3 text-white/80">
                  <CheckCircle2 size={18} className="text-gold shrink-0 mt-0.5" />
                  <span>{t(`landing.ip_bullet_${n}`)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative">
            <div className="glass-card rounded-3xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-flex h-12 w-12 rounded-full bg-gold/15 text-gold items-center justify-center">
                  <Shield size={22} />
                </span>
                <div>
                  <p className="text-white/60 text-xs uppercase tracking-widest">{t("landing.ip_verif_label")}</p>
                  <p className="text-white text-lg">{t("landing.ip_verif_h4")}</p>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  { key: "ip_level_0", c: "text-white/40" },
                  { key: "ip_level_1", c: "text-white/70" },
                  { key: "ip_level_2", c: "text-gold" },
                  { key: "ip_level_3", c: "text-gold" },
                  { key: "ip_level_4", c: "text-gold" },
                  { key: "ip_level_5", c: "text-gold" },
                ].map((s, i) => (
                  <div key={s.key} className="flex items-center justify-between border-b border-white/5 pb-3">
                    <span className={s.c}>{t(`landing.${s.key}`)}</span>
                    <span className="text-xs text-white/40">{t("landing.ip_level_prefix")} {i + 1}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative border-t border-white/5 bg-gradient-to-b from-[#080F1B] via-[#0B1320] to-[#0B1320]">
        <div className="mx-auto max-w-4xl px-6 py-24 text-center">
          <p className="eyebrow text-gold">{t("landing.cta_start_today")}</p>
          <h2 className="font-display text-4xl md:text-6xl mt-4 tracking-tight text-white">
            {t("landing.cta_h2_a")} <span className="italic text-gold">{t("landing.cta_h2_b")}</span>
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/registro?role=creator"
              className="gold-cta inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold"
              data-testid="button-cta-publish"
            >
              {t("landing.cta_publish")} <ArrowRight size={18} />
            </Link>
            <Link
              to="/registro?role=sponsor"
              className="ghost-cta inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold"
              data-testid="button-cta-sponsor"
            >
              {t("landing.cta_find_story")}
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
